import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Table, Tag, Button, Input, Select, Row, Col, DatePicker, Drawer, Descriptions,
  Space, Tooltip, Segmented, Switch,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, HistoryOutlined, TableOutlined, CodeOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import dayjs from 'dayjs';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import TableExportButton from '../../components/TableExportButton';
import { libelleEvenement } from './libelleEvenement';
import axiosInstance from '../../variables/api/axiosInstance';
import { listerActivites, listerModules, TYPES_ACTION } from '../../services/journalActiviteService';

const { RangePicker } = DatePicker;

/**
 * Journal d'activite (audit trail) - qui a fait quoi, quand, depuis ou.
 *
 * Les administrateurs n'ont pas acces aux logs applicatifs du serveur : cet
 * ecran est leur seule fenetre sur l'activite reelle de la plateforme
 * (connexions, echecs de connexion, creations, modifications, suppressions).
 * La table backend est INSERT-ONLY : rien ne se corrige ni ne se purge ici,
 * volontairement - un journal d'audit editable ne prouve rien.
 *
 * Deux vues sur les memes evenements :
 *  - « Console » (defaut) : presentation en fichier de log, calquee sur les
 *    visualiseurs des grands logiciels (fond sombre, monospace, une ligne par
 *    evenement `[date] [NIVEAU] [module] message`, selecteur « Afficher N »,
 *    suivi temps reel). C'est la lecture en continu : que se passe-t-il ?
 *  - « Tableau » : memes lignes en colonnes triables/exportables. C'est la
 *    lecture d'enquete : retrouver un evenement precis.
 */

/** Police monospace assumee : on affiche un log, pas un chiffre d'interface. */
const MONO = "ui-monospace, SFMono-Regular, 'Cascadia Mono', Menlo, Consolas, monospace";

/**
 * Niveau façon fichier de log, derive de l'evenement : un echec est une ERROR,
 * une suppression merite un WARN, les consultations et deconnexions sont du
 * flux de fond (DEBUG attenue, comme dans les visualiseurs de logs), le reste
 * est de l'INFO.
 */
const niveauDe = (rec) => {
  if (rec.succes === false) return { tag: 'ERROR', couleur: '#f97583', dim: false };
  if (rec.typeAction === 'SUPPRESSION') return { tag: 'WARN', couleur: '#d29922', dim: false };
  if (rec.typeAction === 'CONSULTATION') return { tag: 'DEBUG', couleur: '#8b949e', dim: true };
  if (rec.typeAction === 'DECONNEXION') return { tag: 'INFO', couleur: '#8b949e', dim: true };
  return { tag: 'INFO', couleur: '#e6edf3', dim: false };
};

export default function JournalActivite() {
  const { t } = useTranslation('journalActivite');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

  const [vue, setVue] = useState('console');
  const [nbLignes, setNbLignes] = useState(1000);
  const [suivre, setSuivre] = useState(true);
  const consoleRef = useRef(null);

  const [recherche, setRecherche] = useState('');
  const [filtreUtilisateur, setFiltreUtilisateur] = useState(null);
  const [filtreAction, setFiltreAction] = useState(null);
  const [filtreModule, setFiltreModule] = useState(null);
  const [periode, setPeriode] = useState(null);

  const [utilisateurs, setUtilisateurs] = useState([]);
  const [modules, setModules] = useState([]);

  const [detail, setDetail] = useState(null);

  const construireParams = useCallback(() => {
    const params = {};
    if (recherche) params.recherche = recherche;
    if (filtreUtilisateur) params.utilisateur = filtreUtilisateur;
    if (filtreAction) params.typeAction = filtreAction;
    if (filtreModule) params.module = filtreModule;
    // Bornes inclusives jour entier : le backend compare des timestamps.
    if (periode?.[0]) params.dateDebut = periode[0].format('YYYY-MM-DD 00:00:00');
    if (periode?.[1]) params.dateFin = periode[1].format('YYYY-MM-DD 23:59:59');
    return params;
  }, [recherche, filtreUtilisateur, filtreAction, filtreModule, periode]);

  const load = useCallback(async (page = 0, size = 25, { silencieux = false } = {}) => {
    if (!silencieux) setLoading(true);
    try {
      const result = await listerActivites(page, size, construireParams());
      setData(result.items);
      setTotal(result.totalElements);
      setPagination((p) => ({ ...p, current: page + 1, pageSize: size }));
    } catch {
      if (!silencieux) toast.error(i18n.t('journalActivite:messages.erreurChargement'));
    } finally {
      if (!silencieux) setLoading(false);
    }
  }, [construireParams]);

  // Chargement initial + rechargement quand filtres ou mode changent.
  useEffect(() => {
    if (vue === 'console') load(0, nbLignes);
    else load(0, pagination.pageSize);
  }, [load, vue, nbLignes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suivi temps reel de la vue console : rafraichissement silencieux - pas de
  // spinner qui clignote toutes les 5 s, le flux se met a jour comme un tail -f.
  useEffect(() => {
    if (vue !== 'console' || !suivre) return undefined;
    const id = setInterval(() => load(0, nbLignes, { silencieux: true }), 5000);
    return () => clearInterval(id);
  }, [vue, suivre, nbLignes, load]);

  // Un log se lit en bas : on y colle apres chaque arrivee de donnees.
  useEffect(() => {
    if (vue === 'console' && suivre && consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [data, vue, suivre]);

  useEffect(() => {
    // Options des filtres, silencieuses en cas d'echec : le journal reste
    // consultable meme si une des listes ne charge pas.
    axiosInstance.get('/v1/utilisateurs', { skipErrorToast: true })
      .then((res) => setUtilisateurs(res.data?.data || []))
      .catch(() => { });
    listerModules().then(setModules).catch(() => { });
  }, []);

  const handleTableChange = (p) => load(p.current - 1, p.pageSize);

  const reinitialiser = () => {
    setRecherche('');
    setFiltreUtilisateur(null);
    setFiltreAction(null);
    setFiltreModule(null);
    setPeriode(null);
  };

  const tagAction = (val) => {
    const t = TYPES_ACTION[val] || { label: val, color: 'default' };
    return <Tag color={t.color}>{t.label}</Tag>;
  };

  /* ── Vue console ─────────────────────────────────────────────────────────── */

  // Le backend sert du plus recent au plus ancien (logique de tableau) ; un
  // fichier de log se lit dans l'ordre chronologique, le plus recent en bas.
  const lignesConsole = [...data].reverse();

  const ligneConsole = (rec) => {
    const n = niveauDe(rec);
    const date = rec.dateAction ? dayjs(rec.dateAction).format('YYYY-MM-DD HH:mm:ss') : '-';
    const message = libelleEvenement(rec);
    return (
      <div
        key={rec.id}
        onClick={() => setDetail(rec)}
        style={{
          padding: '1px 12px', cursor: 'pointer', whiteSpace: 'pre-wrap',
          wordBreak: 'break-all', opacity: n.dim ? 0.55 : 1,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#21262d'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ color: '#8b949e' }}>[{date}]</span>
        {' '}
        <span style={{ color: n.tag === 'ERROR' ? '#f97583' : n.tag === 'WARN' ? '#d29922' : '#79c0ea' }}>
          [{n.tag}]
        </span>
        {' '}
        <span style={{ color: '#7ee787' }}>[{rec.module || t('console.moduleAutre')}]</span>
        {' '}
        <span style={{ color: n.couleur }}>{message}</span>
        {rec.login && <span style={{ color: '#d2a8ff' }}> - {rec.login}</span>}
        {/* LA METHODE ET LE CHEMIN ONT DISPARU D'ICI. Ils fermaient la ligne d'un
            « GET /v1/sinistres/... » qui n'apprenait rien au lecteur et donnait la
            carte du serveur a quiconque ouvre le journal. Le module, deja affiche
            entre crochets, dit de quel ecran il s'agit. */}
        {rec.adresseIp && <span style={{ color: '#8b949e' }}>{' · IP `'}{rec.adresseIp}{'`'}</span>}
      </div>
    );
  };

  const vueConsole = (
    <div style={{ background: '#0d1117', borderRadius: 12, overflow: 'hidden', border: '1px solid #21262d' }}>
      {/* Barre d'onglet façon visualiseur de logs : nom du flux a gauche,
          « Afficher N » et suivi a droite - comme la capture de reference. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', background: '#161b22', borderBottom: '1px solid #21262d',
      }}>
        <span style={{
          fontFamily: MONO, fontSize: 13, color: '#e6edf3', background: '#0d1117',
          padding: '4px 14px', borderRadius: '8px 8px 0 0', border: '1px solid #21262d', borderBottom: 'none',
        }}>
          journal-activite.log
        </span>
        <Space size={12}>
          <span style={{ color: '#8b949e', fontSize: 12 }}>{t('console.afficher')}</span>
          <Select
            size="small"
            value={nbLignes}
            onChange={setNbLignes}
            style={{ width: 90 }}
            options={[100, 500, 1000, 5000].map((n) => ({ value: n, label: n }))}
          />
          <Tooltip title={t('console.suivreAide')}>
            <span style={{ color: '#8b949e', fontSize: 12 }}>
              {t('console.suivre')}{' '}
              <Switch size="small" checked={suivre} onChange={setSuivre} />
            </span>
          </Tooltip>
          <Button
            size="small" ghost icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => load(0, nbLignes)}
          />
        </Space>
      </div>

      <div
        ref={consoleRef}
        style={{
          fontFamily: MONO, fontSize: 12.5, lineHeight: 1.75,
          height: '58vh', overflowY: 'auto', padding: '8px 0',
        }}
      >
        {lignesConsole.length === 0 && (
          <div style={{ color: '#8b949e', padding: '24px 16px' }}>
            {loading ? t('console.chargement') : t('console.aucunEvenement')}
          </div>
        )}
        {lignesConsole.map(ligneConsole)}
      </div>
    </div>
  );

  /* ── Vue tableau ─────────────────────────────────────────────────────────── */

  const columns = [
    {
      title: t('champs.date'),
      dataIndex: 'dateAction',
      key: 'dateAction',
      width: 150,
      format: 'datetime',
      render: (v) => (
        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {v ? dayjs(v).format('DD/MM/YYYY HH:mm:ss') : '-'}
        </span>
      ),
    },
    {
      title: t('champs.utilisateur'),
      key: 'utilisateur',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 600 }}>{rec.nomComplet || rec.login || '-'}</div>
          {rec.nomComplet && rec.login && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>{rec.login}</div>
          )}
        </div>
      ),
      export: (_, rec) => rec.nomComplet || rec.login || '',
    },
    {
      title: t('champs.action'),
      dataIndex: 'typeAction',
      key: 'typeAction',
      width: 150,
      render: tagAction,
      export: (v) => TYPES_ACTION[v]?.label || v,
    },
    {
      title: t('champs.module'),
      dataIndex: 'module',
      key: 'module',
      width: 160,
      render: (v) => v || '-',
    },
    {
      /* LA COLONNE NE LIT PLUS `chemin`. Elle affichait la route de la requete,
         tronquee puis plus du tout : l'infobulle et l'export suivent, un fichier
         circulant plus loin qu'un ecran. `libelleEvenement` rend la description,
         ou l'action et le module a defaut - jamais une ligne vide. */
      title: t('champs.detail'),
      dataIndex: 'description',
      key: 'detail',
      ellipsis: true,
      render: (_, rec) => {
        const libelle = libelleEvenement(rec);
        return (
          <Tooltip title={libelle}>
            <span style={{ fontSize: 12, color: '#374151' }}>{libelle}</span>
          </Tooltip>
        );
      },
      export: (_, rec) => libelleEvenement(rec),
    },
    {
      title: t('champs.adresseIp'),
      dataIndex: 'adresseIp',
      key: 'adresseIp',
      width: 130,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v || '-'}</span>,
    },
    {
      title: t('champs.resultat'),
      dataIndex: 'succes',
      key: 'succes',
      width: 100,
      format: 'bool',
      render: (v) => (
        <span className={`cb-badge ${v ? 'cb-badge-success' : 'cb-badge-danger'}`}>
          {v ? t('valeurs.succes') : t('valeurs.echec')}
        </span>
      ),
      export: (v) => (v ? t('valeurs.succes') : t('valeurs.echec')),
    },
  ];

  const vueTableau = (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      pagination={{
        ...pagination,
        total,
        showSizeChanger: true,
        showTotal: (n) => t('tableau.total', { nb: n }),
      }}
      onChange={handleTableChange}
      onRow={(rec) => ({ onClick: () => setDetail(rec), style: { cursor: 'pointer' } })}
      size="small"
      scroll={{ x: 1000 }}
    />
  );

  /* ── Page ────────────────────────────────────────────────────────────────── */

  return (
    <Layout title={t('page.titre')}>
      <PageHeader
        titre={t('page.titre')}
        icone={<HistoryOutlined />}
        compte={total}
        unite={t('page.unite')}
        unitePluriel={t('page.unitePluriel')}
        actions={(
          <Segmented
            value={vue}
            onChange={setVue}
            options={[
              { value: 'console', label: t('vues.console'), icon: <CodeOutlined /> },
              { value: 'tableau', label: t('vues.tableau'), icon: <TableOutlined /> },
            ]}
          />
        )}
      />

      <div className="cb-card" style={{ padding: 16 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={6}>
            <Input
              placeholder={t('filtres.recherche')}
              prefix={<SearchOutlined />}
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              onPressEnter={() => (vue === 'console' ? load(0, nbLignes) : load(0, pagination.pageSize))}
              allowClear
            />
          </Col>
          <Col xs={12} md={5}>
            <Select
              placeholder={t('champs.utilisateur')} allowClear showSearch style={{ width: '100%' }}
              value={filtreUtilisateur}
              onChange={setFiltreUtilisateur}
              optionFilterProp="label"
              options={utilisateurs.map((u) => ({
                value: u.email,
                label: `${u.nom || ''} ${u.prenoms || ''}`.trim() || u.email,
              }))}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder={t('filtres.typeAction')} allowClear style={{ width: '100%' }}
              value={filtreAction}
              onChange={setFiltreAction}
              options={Object.entries(TYPES_ACTION).map(([k, v]) => ({
                value: k,
                label: <Tag color={v.color} style={{ marginRight: 0 }}>{v.label}</Tag>,
              }))}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder={t('champs.module')} allowClear showSearch style={{ width: '100%' }}
              value={filtreModule}
              onChange={setFiltreModule}
              options={modules.map((m) => ({ value: m, label: m }))}
            />
          </Col>
          <Col xs={12} md={5}>
            <Space.Compact block>
              <RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={[t('filtres.debut'), t('filtres.fin')]}
                value={periode}
                onChange={setPeriode}
              />
              <Tooltip title={t('filtres.reinitialiser')}>
                <Button icon={<ReloadOutlined />} onClick={reinitialiser} />
              </Tooltip>
            </Space.Compact>
          </Col>
        </Row>

        {vue === 'console' ? vueConsole : vueTableau}
      </div>

      <Drawer
        title={detail ? (
          <Space>
            <span>{detail.nomComplet || detail.login || t('detail.evenement')}</span>
            {tagAction(detail.typeAction)}
          </Space>
        ) : ''}
        width={620}
        open={!!detail}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <Descriptions column={1} size="small" bordered labelStyle={{ fontWeight: 600, width: 170 }}>
            <Descriptions.Item label={t('champs.date')}>
              {detail.dateAction ? dayjs(detail.dateAction).format('DD/MM/YYYY HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('champs.utilisateur')}>{detail.nomComplet || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.login')}>{detail.login || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.organisme')}>{detail.organismeLibelle || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.action')}>{tagAction(detail.typeAction)}</Descriptions.Item>
            <Descriptions.Item label={t('champs.module')}>{detail.module || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.description')}>{libelleEvenement(detail)}</Descriptions.Item>
            <Descriptions.Item label={t('champs.statutHttp')}>{detail.statutHttp ?? '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.resultat')}>
              <span className={`cb-badge ${detail.succes ? 'cb-badge-success' : 'cb-badge-danger'}`}>
                {detail.succes ? t('valeurs.succes') : t('valeurs.echec')}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('champs.duree')}>
              {detail.dureeMs != null ? `${detail.dureeMs} ms` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('champs.adresseIp')}>{detail.adresseIp || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('champs.navigateur')}>
              <span style={{ fontSize: 12, wordBreak: 'break-all' }}>{detail.userAgent || '-'}</span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/*  Export flottant  */}
      {/* `floating` suffit : le composant pose lui-meme l'enveloppe, le coin bas-droite
          et la reserve de place qui empeche le bas du tableau de passer sous le bouton.
          Une enveloppe ecrite ici la doublerait, et un test l'interdit. */}
      <TableExportButton
        data={data}
        columns={columns}
        filename="journal-activite"
        title={t('page.titre')}
        floating
        count={total}
        loadingLabel={t('page.preparationExport')}
      />
    </Layout>
  );
}
